import _defineProperty from "/codebuild/output/src20/src/s3/01/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { tracingContextFromHeaders, logger, dropUndefinedKeys } from '@sentry/utils';
import { DEBUG_BUILD } from '../debug-build.js';
import { getCurrentScope, withScope } from '../exports.js';
import { getCurrentHub } from '../hub.js';
import { handleCallbackErrors } from '../utils/handleCallbackErrors.js';
import { hasTracingEnabled } from '../utils/hasTracingEnabled.js';
import { spanTimeInputToSeconds } from '../utils/spanUtils.js';

/**
 * Wraps a function with a transaction/span and finishes the span after the function is done.
 *
 * Note that if you have not enabled tracing extensions via `addTracingExtensions`
 * or you didn't set `tracesSampleRate`, this function will not generate spans
 * and the `span` returned from the callback will be undefined.
 *
 * This function is meant to be used internally and may break at any time. Use at your own risk.
 *
 * @internal
 * @private
 *
 * @deprecated Use `startSpan` instead.
 */
function trace(context, callback) {
  var onError = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : function () {};
  var afterFinish = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : function () {};
  var hub = getCurrentHub();
  var scope = getCurrentScope();
  // eslint-disable-next-line deprecation/deprecation
  var parentSpan = scope.getSpan();
  var ctx = normalizeContext(context);
  var activeSpan = createChildSpanOrTransaction(hub, parentSpan, ctx);

  // eslint-disable-next-line deprecation/deprecation
  scope.setSpan(activeSpan);
  return handleCallbackErrors(function () {
    return callback(activeSpan);
  }, function (error) {
    activeSpan && activeSpan.setStatus('internal_error');
    onError(error, activeSpan);
  }, function () {
    activeSpan && activeSpan.end();
    // eslint-disable-next-line deprecation/deprecation
    scope.setSpan(parentSpan);
    afterFinish();
  });
}

/**
 * Wraps a function with a transaction/span and finishes the span after the function is done.
 * The created span is the active span and will be used as parent by other spans created inside the function
 * and can be accessed via `Sentry.getSpan()`, as long as the function is executed while the scope is active.
 *
 * If you want to create a span that is not set as active, use {@link startInactiveSpan}.
 *
 * Note that if you have not enabled tracing extensions via `addTracingExtensions`
 * or you didn't set `tracesSampleRate`, this function will not generate spans
 * and the `span` returned from the callback will be undefined.
 */
function startSpan(context, callback) {
  var ctx = normalizeContext(context);
  return withScope(context.scope, function (scope) {
    var hub = getCurrentHub();
    // eslint-disable-next-line deprecation/deprecation
    var parentSpan = scope.getSpan();
    var activeSpan = createChildSpanOrTransaction(hub, parentSpan, ctx);
    // eslint-disable-next-line deprecation/deprecation
    scope.setSpan(activeSpan);
    return handleCallbackErrors(function () {
      return callback(activeSpan);
    }, function () {
      // Only update the span status if it hasn't been changed yet
      if (activeSpan && (!activeSpan.status || activeSpan.status === 'ok')) {
        activeSpan.setStatus('internal_error');
      }
    }, function () {
      return activeSpan && activeSpan.end();
    });
  });
}

/**
 * @deprecated Use {@link startSpan} instead.
 */
var startActiveSpan = startSpan;

/**
 * Similar to `Sentry.startSpan`. Wraps a function with a transaction/span, but does not finish the span
 * after the function is done automatically. You'll have to call `span.end()` manually.
 *
 * The created span is the active span and will be used as parent by other spans created inside the function
 * and can be accessed via `Sentry.getActiveSpan()`, as long as the function is executed while the scope is active.
 *
 * Note that if you have not enabled tracing extensions via `addTracingExtensions`
 * or you didn't set `tracesSampleRate`, this function will not generate spans
 * and the `span` returned from the callback will be undefined.
 */
function startSpanManual(context, callback) {
  var ctx = normalizeContext(context);
  return withScope(context.scope, function (scope) {
    var hub = getCurrentHub();
    // eslint-disable-next-line deprecation/deprecation
    var parentSpan = scope.getSpan();
    var activeSpan = createChildSpanOrTransaction(hub, parentSpan, ctx);
    // eslint-disable-next-line deprecation/deprecation
    scope.setSpan(activeSpan);
    function finishAndSetSpan() {
      activeSpan && activeSpan.end();
    }
    return handleCallbackErrors(function () {
      return callback(activeSpan, finishAndSetSpan);
    }, function () {
      // Only update the span status if it hasn't been changed yet, and the span is not yet finished
      if (activeSpan && !activeSpan.endTimestamp && (!activeSpan.status || activeSpan.status === 'ok')) {
        activeSpan.setStatus('internal_error');
      }
    });
  });
}

/**
 * Creates a span. This span is not set as active, so will not get automatic instrumentation spans
 * as children or be able to be accessed via `Sentry.getSpan()`.
 *
 * If you want to create a span that is set as active, use {@link startSpan}.
 *
 * Note that if you have not enabled tracing extensions via `addTracingExtensions`
 * or you didn't set `tracesSampleRate` or `tracesSampler`, this function will not generate spans
 * and the `span` returned from the callback will be undefined.
 */
function startInactiveSpan(context) {
  if (!hasTracingEnabled()) {
    return undefined;
  }
  var ctx = normalizeContext(context);
  var hub = getCurrentHub();
  var parentSpan = context.scope ?
  // eslint-disable-next-line deprecation/deprecation
  context.scope.getSpan() : getActiveSpan();
  return parentSpan ?
  // eslint-disable-next-line deprecation/deprecation
  parentSpan.startChild(ctx) :
  // eslint-disable-next-line deprecation/deprecation
  hub.startTransaction(ctx);
}

/**
 * Returns the currently active span.
 */
function getActiveSpan() {
  // eslint-disable-next-line deprecation/deprecation
  return getCurrentScope().getSpan();
}

/**
 * Continue a trace from `sentry-trace` and `baggage` values.
 * These values can be obtained from incoming request headers,
 * or in the browser from `<meta name="sentry-trace">` and `<meta name="baggage">` HTML tags.
 *
 * The callback receives a transactionContext that may be used for `startTransaction` or `startSpan`.
 */
function continueTrace(_ref, callback) {
  var sentryTrace = _ref.sentryTrace,
    baggage = _ref.baggage;
  var currentScope = getCurrentScope();
  var _tracingContextFromHe = tracingContextFromHeaders(sentryTrace, baggage),
    traceparentData = _tracingContextFromHe.traceparentData,
    dynamicSamplingContext = _tracingContextFromHe.dynamicSamplingContext,
    propagationContext = _tracingContextFromHe.propagationContext;
  currentScope.setPropagationContext(propagationContext);
  if (DEBUG_BUILD && traceparentData) {
    logger.log("[Tracing] Continuing trace ".concat(traceparentData.traceId, "."));
  }
  var transactionContext = _objectSpread(_objectSpread({}, traceparentData), {}, {
    metadata: dropUndefinedKeys({
      dynamicSamplingContext: traceparentData && !dynamicSamplingContext ? {} : dynamicSamplingContext
    })
  });
  if (!callback) {
    return transactionContext;
  }
  return callback(transactionContext);
}
function createChildSpanOrTransaction(hub, parentSpan, ctx) {
  if (!hasTracingEnabled()) {
    return undefined;
  }
  return parentSpan ?
  // eslint-disable-next-line deprecation/deprecation
  parentSpan.startChild(ctx) :
  // eslint-disable-next-line deprecation/deprecation
  hub.startTransaction(ctx);
}

/**
 * This converts StartSpanOptions to TransactionContext.
 * For the most part (for now) we accept the same options,
 * but some of them need to be transformed.
 *
 * Eventually the StartSpanOptions will be more aligned with OpenTelemetry.
 */
function normalizeContext(context) {
  if (context.startTime) {
    var ctx = _objectSpread({}, context);
    ctx.startTimestamp = spanTimeInputToSeconds(context.startTime);
    delete ctx.startTime;
    return ctx;
  }
  return context;
}
export { continueTrace, getActiveSpan, startActiveSpan, startInactiveSpan, startSpan, startSpanManual, trace };